// «Агенты»: сетка агентов по направлениям + создание нового агента.
import { useEffect, useId, useMemo, useState } from 'react'
import { api } from '../api.ts'
import { AgentModels } from '../components/AgentModels.tsx'
import { AgentAvatar } from '../components/AgentAvatar.tsx'
import { Modal } from '../components/Layer.tsx'
import { ModelInput } from '../components/ModelInput.tsx'
import { Icon } from '../components/Icon.tsx'
import { Badge, Button, Empty, Field, Loading, PageHeader, cx } from '../components/ui.tsx'
import { useBusy } from '../lib/hooks.ts'
import { AGENT_KIND_LABELS, AGENT_KIND_TONES, AGENT_STATUS_LABELS, formatUsd } from '../lib/format.ts'
import { Link, TaskLink, navigate } from '../router.tsx'
import { refreshAgents, upsertAgent, useLive } from '../store.ts'
import { toast, toastError } from '../toast.ts'
import type { Agent, AgentKind } from '../types.ts'

const TOP_KINDS: AgentKind[] = ['orchestrator', 'reviewer', 'librarian']

export function AgentStatusPill({ agent }: { agent: Agent }) {
  if (agent.status === 'paused')
    return (
      <Badge tone="warn">
        <Icon name="pause" size={11} />
        {AGENT_STATUS_LABELS.paused}
      </Badge>
    )
  if (agent.status === 'busy' || (agent.running_jobs ?? 0) > 0)
    return (
      <Badge tone="ok" dot>
        {AGENT_STATUS_LABELS.busy}
      </Badge>
    )
  return <Badge tone="neutral">{AGENT_STATUS_LABELS.idle}</Badge>
}

function AgentCard({ agent }: { agent: Agent }) {
  const tasks = agent.current_task_ids ?? []
  return (
    <div
      onClick={() => navigate(`/agents/${encodeURIComponent(agent.id)}`)}
      className={cx(
        'group relative flex cursor-pointer flex-col gap-2.5 rounded-[12px] border border-line bg-surface px-4 py-3.5 shadow-card transition-[border-color,box-shadow] hover:border-line-strong hover:shadow-pop',
      )}
    >
      <div className="flex items-start gap-2.5">
        <AgentAvatar agent={agent} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <Link
              to={`/agents/${encodeURIComponent(agent.id)}`}
              className="truncate text-[14px] font-semibold group-hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {agent.name}
            </Link>
          </div>
          <div className="line-clamp-2 text-[12px] leading-snug text-muted">{agent.title || agent.mission || '—'}</div>
        </div>
        <AgentStatusPill agent={agent} />
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge tone={AGENT_KIND_TONES[agent.kind]}>{AGENT_KIND_LABELS[agent.kind]}</Badge>
        <AgentModels agent={agent} />
        {agent.max_parallel > 1 ? <span className="text-[11px] text-subtle">×{agent.max_parallel}</span> : null}
      </div>
      <div className="mt-auto flex items-center gap-3 border-t border-line pt-2.5 text-[12px] text-muted">
        <span className="tnum inline-flex items-center gap-1" title="Запущено сейчас">
          <Icon name="play" size={11} />
          {agent.running_jobs ?? 0}
        </span>
        <span className="tnum inline-flex items-center gap-1" title="В очереди">
          <Icon name="list" size={12} />
          {agent.queued_jobs ?? 0}
        </span>
        {tasks.length ? (
          <span className="flex min-w-0 items-center gap-1 truncate" onClick={(e) => e.stopPropagation()}>
            {tasks.slice(0, 3).map((t) => (
              <TaskLink key={t} id={t} className="font-mono text-[11px] hover:text-fg hover:underline">
                OMA-{t}
              </TaskLink>
            ))}
            {tasks.length > 3 ? <span>+{tasks.length - 3}</span> : null}
          </span>
        ) : (
          <span className="text-subtle">нет текущих задач</span>
        )}
        <span className="tnum ml-auto shrink-0" title={`Всего запусков: ${agent.jobs_total ?? 0}`}>
          {formatUsd(agent.cost_total_usd ?? 0)}
        </span>
      </div>
    </div>
  )
}

export function AgentsPage() {
  const agents = useLive((s) => s.agents)
  const loaded = useLive((s) => s.agentsLoaded)
  const directions = useLive((s) => s.directions)
  const [creating, setCreating] = useState(false)
  const [q, setQ] = useState('')

  useEffect(() => {
    void refreshAgents()
  }, [])

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const list = agents.filter(
      (a) =>
        !a.archived &&
        (!needle ||
          a.name.toLowerCase().includes(needle) ||
          a.id.includes(needle) ||
          a.title.toLowerCase().includes(needle)),
    )
    const res: { id: string; label: string; color?: string; agents: Agent[] }[] = []
    const top = list.filter((a) => TOP_KINDS.includes(a.kind))
    top.sort((a, b) => TOP_KINDS.indexOf(a.kind) - TOP_KINDS.indexOf(b.kind) || a.name.localeCompare(b.name, 'ru'))
    if (top.length) res.push({ id: '_top', label: 'Штаб и служебные агенты', agents: top })
    for (const d of directions) {
      const items = list
        .filter((a) => !TOP_KINDS.includes(a.kind) && a.direction_id === d.id)
        .sort((a, b) => Number(b.kind === 'lead') - Number(a.kind === 'lead') || a.name.localeCompare(b.name, 'ru'))
      if (items.length) res.push({ id: d.id, label: d.name, color: d.color, agents: items })
    }
    const known = new Set(directions.map((d) => d.id))
    const rest = list.filter((a) => !TOP_KINDS.includes(a.kind) && (!a.direction_id || !known.has(a.direction_id)))
    if (rest.length) res.push({ id: '_rest', label: 'Без направления', agents: rest })
    return res
  }, [agents, directions, q])

  const busy = agents.filter((a) => a.status === 'busy' || (a.running_jobs ?? 0) > 0).length

  return (
    <div className="page">
      <PageHeader
        title="Агенты"
        lede={
          <>
            <span className="tnum">{agents.filter((a) => !a.archived).length}</span> агентов ·{' '}
            <span className="tnum">{busy}</span> работают сейчас
          </>
        }
        actions={
          <>
            <input
              type="search"
              className="ctl h-8 w-56 max-sm:w-auto max-sm:flex-1"
              placeholder="Найти агента…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Поиск агентов"
            />
            <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
              Новый агент
            </Button>
          </>
        }
      />

      {!loaded ? (
        <Loading />
      ) : groups.length === 0 ? (
        <Empty icon="user">Агентов не найдено</Empty>
      ) : (
        <div className="flex flex-col gap-6">
          {groups.map((g) => (
            <section key={g.id}>
              <h2 className="ct mb-2.5 flex items-center gap-2">
                <span>{g.label}</span>
                <span className="tnum rounded-full bg-surface-3 px-1.5 text-[11px] leading-[18px] font-semibold tracking-normal text-muted normal-case">
                  {g.agents.length}
                </span>
              </h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {g.agents.map((a) => (
                  <AgentCard key={a.id} agent={a} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {creating ? <NewAgentModal onClose={() => setCreating(false)} /> : null}
    </div>
  )
}

const KINDS: AgentKind[] = ['lead', 'service', 'worker', 'reviewer', 'librarian', 'orchestrator', 'external']

function NewAgentModal({ onClose }: { onClose: () => void }) {
  const directions = useLive((s) => s.directions)
  const services = useLive((s) => s.services)
  const [id, setId] = useState('')
  const [name, setName] = useState('')
  const [kind, setKind] = useState<AgentKind>('service')
  const [direction, setDirection] = useState('')
  const [title, setTitle] = useState('')
  const [avatar, setAvatar] = useState('')
  const [model, setModel] = useState('sonnet')
  const [mission, setMission] = useState('')
  const [instructions, setInstructions] = useState('')
  const [serviceIds, setServiceIds] = useState<string[]>([])
  const [maxParallel, setMaxParallel] = useState(1)
  const [busy, run] = useBusy()
  const f = {
    id: useId(),
    name: useId(),
    kind: useId(),
    dir: useId(),
    title: useId(),
    avatar: useId(),
    model: useId(),
    mission: useId(),
    instr: useId(),
    par: useId(),
  }

  const idValid = /^[a-z0-9]+(-[a-z0-9]+)*$/.test(id)
  const dirServices = services.filter((s) => !direction || s.direction_id === direction)

  const submit = () =>
    run(async () => {
      if (!idValid || !name.trim()) {
        toast('Нужны идентификатор (kebab-case) и имя', 'error')
        return
      }
      try {
        const a = await api.post<Agent>('/api/agents', {
          id,
          name: name.trim(),
          kind,
          direction_id: direction || null,
          title: title.trim(),
          avatar: avatar.trim(),
          model: model || 'sonnet',
          mission: mission.trim(),
          instructions: instructions.trim(),
          service_ids: serviceIds,
          max_parallel: maxParallel,
        })
        upsertAgent(a)
        toast(`Агент «${a.name}» создан`, 'success')
        onClose()
        navigate(`/agents/${encodeURIComponent(a.id)}`)
      } catch (e) {
        toastError(e, 'Не удалось создать агента')
      }
    })

  return (
    <Modal
      title="Новый агент"
      onClose={onClose}
      width="max-w-2xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" onClick={() => void submit()} loading={busy} disabled={!idValid || !name.trim()}>
            Создать агента
          </Button>
        </>
      }
    >
      <form
        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <Field label="Имя" htmlFor={f.name}>
          <input id={f.name} className="ctl" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ценовик" data-autofocus />
        </Field>
        <Field
          label="Идентификатор"
          htmlFor={f.id}
          hint={id && !idValid ? <span className="text-danger">только латиница, цифры и дефисы: pricing-lead</span> : 'kebab-case, не меняется'}
        >
          <input id={f.id} className="ctl font-mono" value={id} onChange={(e) => setId(e.target.value.toLowerCase())} placeholder="pricing-lead" />
        </Field>
        <Field label="Роль" htmlFor={f.kind}>
          <select id={f.kind} className="ctl" value={kind} onChange={(e) => setKind(e.target.value as AgentKind)}>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {AGENT_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Направление" htmlFor={f.dir}>
          <select id={f.dir} className="ctl" value={direction} onChange={(e) => setDirection(e.target.value)}>
            <option value="">— без направления —</option>
            {directions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Должность / подпись" htmlFor={f.title} className="sm:col-span-2">
          <input id={f.title} className="ctl" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Отвечает за цены и остатки на маркетплейсах" />
        </Field>
        <Field label="Монограмма" htmlFor={f.avatar} hint="1–2 буквы; пусто — по имени">
          <input id={f.avatar} className="ctl" value={avatar} onChange={(e) => setAvatar(e.target.value.replace(/[^\p{L}\p{N}]/gu, '').slice(0, 2))} placeholder="ЦН" maxLength={2} />
        </Field>
        <Field label="Параллельных запусков" htmlFor={f.par}>
          <input
            id={f.par}
            type="number"
            min={1}
            max={10}
            className="ctl"
            value={maxParallel}
            onChange={(e) => setMaxParallel(Math.max(1, Number(e.target.value) || 1))}
          />
        </Field>
        <Field label="Модель Claude" hint="Codex использует соответствующий уровень раннера: Opus → усиленная модель, Sonnet/Haiku → основная." htmlFor={f.model} className="sm:col-span-2">
          <ModelInput id={f.model} value={model} onChange={setModel} />
        </Field>
        <Field label="Миссия" htmlFor={f.mission} className="sm:col-span-2" hint="Зачем этот агент существует — одна-две фразы.">
          <textarea id={f.mission} className="ctl min-h-[56px]" value={mission} onChange={(e) => setMission(e.target.value)} />
        </Field>
        <Field label="Инструкции" htmlFor={f.instr} className="sm:col-span-2" hint="Правила работы, ограничения, куда смотреть. Markdown.">
          <textarea id={f.instr} className="ctl min-h-[110px] font-mono text-[12px]" value={instructions} onChange={(e) => setInstructions(e.target.value)} />
        </Field>
        {dirServices.length ? (
          <div className="flex flex-col gap-1 sm:col-span-2">
            <span className="text-[12px] font-medium text-muted">Сервисы в ответственности</span>
            <div className="flex max-h-32 flex-wrap gap-1.5 overflow-y-auto">
              {dirServices.map((s) => {
                const on = serviceIds.includes(s.id)
                return (
                  <button
                    key={s.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setServiceIds((v) => (on ? v.filter((x) => x !== s.id) : [...v, s.id]))}
                    className={cx(
                      'max-w-full rounded border px-1.5 py-0.5 text-left font-mono text-[11px] [overflow-wrap:anywhere]',
                      on ? 'border-accent bg-accent-soft text-accent' : 'border-line bg-surface-2 text-muted hover:text-fg',
                    )}
                  >
                    {s.name}
                  </button>
                )
              })}
            </div>
          </div>
        ) : null}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  )
}
